import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Divider,
  Grid,
  Stack,
  Avatar,
  IconButton,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Snackbar,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, api } from '../context/AuthContext';
import {
  Work,
  LocationOn,
  AttachMoney,
  InfoOutlined,
  Business,
  Schedule,
  Description,
  List,
  Payments,
  HomeWork,
  Launch,
  CheckCircleOutline,
  Share,
  BusinessCenter,
  VerifiedRounded,
} from '@mui/icons-material';

function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [resume, setResume] = useState(null);

  const [resumeName, setResumeName] = useState('');
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [fileValidationError, setFileValidationError] = useState('');

  const [education, setEducation] = useState('');
  const [experience, setExperience] = useState('');
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showFinalForm, setShowFinalForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasApplied, setHasApplied] = useState(false);
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    async function fetchJob() {
      try {
        const response = await api.get(`/jobs/${id}`);
        setJob(response.data);

        const qs = response.data?.screening_questions;
        if (Array.isArray(qs)) {
          setScreeningQuestions(qs);
        } else if (typeof qs === 'string') {
          try {
            setScreeningQuestions(JSON.parse(qs));
          } catch (e) {
            setScreeningQuestions([]);
          }
        } else {
          setScreeningQuestions([]);
        }

        // ADDED: Check application status if user is logged in
        if (isAuthenticated) {
          const statusCheck = await api.get(`/applications/check/${id}`);
          if (statusCheck.data.hasApplied) {
            setHasApplied(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch job:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchJob();
  }, [id, isAuthenticated]); // Added isAuthenticated to dependency array

  const resetApplyFlow = () => {
    setError('');
    setSuccess('');
    setCurrentQuestionIndex(0);
    setShowFinalForm(false);
    setScreeningAnswers({});
    setEducation('');
    setExperience('');
    setResume(null);
    setResumeName('');
    setFileValidationError('');
  };

  const handleScreeningAnswerChange = (key, value) => {
    setScreeningAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getExperienceYears = () => {
    const rawValue =
        screeningAnswers.totalExperience ||
        screeningAnswers.experience ||
        experience ||
        '';

    const text = String(rawValue).trim().toLowerCase();
    if (!text) return 0;
    if (text.includes('fresher')) return 0;

    const match = text.match(/(\d+(\.\d+)?)/);
    if (match) return Number(match[1]);

    return text ? 1 : 0;
  };

  const currentQuestion = screeningQuestions[currentQuestionIndex];

  const hasScreeningQuestions =
      Array.isArray(job?.screening_questions) &&
      job.screening_questions
          .map((q) => String(q).trim())
          .filter(Boolean).length > 0;

  const handleNextScreening = () => {
    setError('');
    setSuccess('');

    if (!screeningQuestions.length) {
      setShowFinalForm(true);
      return;
    }

    if (!currentQuestion) {
      setShowFinalForm(true);
      return;
    }

    if (currentQuestion.key === 'noticePeriod') {
      const years = getExperienceYears();
      if (years <= 0) {
        if (currentQuestionIndex < screeningQuestions.length - 1) {
          setCurrentQuestionIndex((prev) => prev + 1);
        } else {
          setShowFinalForm(true);
        }
        return;
      }
    }

    const answer = screeningAnswers[currentQuestion.key];
    if (!answer || !String(answer).trim()) {
      setError('Please answer the current question');
      return;
    }

    if (currentQuestionIndex < screeningQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setShowFinalForm(true);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileValidationError('');

    const isPDF = (file.type === 'application/pdf') ||
        (file.name.toLowerCase().endsWith('.pdf'));

    if (!isPDF) {
      setFileValidationError('⚠️ Only PDF resumes are supported. Please upload a PDF file to enable AI resume analysis and scoring.');
      setResume(null);
      setResumeName(file.name);
      return;
    }

    // 2. Filename check (starts with alphabet)
    if (!/^[a-zA-Z]/.test(file.name)) {
      setFileValidationError('⚠️ Invalid filename. Please rename your resume so that it starts with a letter (e.g., Madhu_Resume.pdf).');
      setResume(null);
      setResumeName(file.name);
      return;
    }

    setResume(file);
    setResumeName(file.name);
  };

  const isSubmitDisabled = !education.trim() || !experience.trim() || !resume || !!fileValidationError;

  async function handleApply() {
    setError('');
    setSuccess('');

    if (!isAuthenticated) {
      alert('Please login first to apply.');
      navigate('/login');
      return;
    }

    if (!education.trim()) {
      setError("Education is required");
      return;
    }

    if (!experience.trim()) {
      setError("Previous experience is required");
      return;
    }

    if (!resume) {
      setError("Resume is required");
      return;
    }
    try {


      const formData = new FormData();

      formData.append("jobId", id);
      formData.append("education", education);
      formData.append("experience", experience);
      formData.append("screening_answers", JSON.stringify(screeningAnswers));
      formData.append("resume", resume);

      // Forward UTM / source tracking data captured on the public job page
      const utmKeys = ['source', 'utm_source', 'utm_medium', 'utm_campaign', 'referrer_url'];
      utmKeys.forEach(key => {
        const val = sessionStorage.getItem(`exihire_${key}`);
        if (val) formData.append(key, val);
      });

      await api.post("/applications", formData);

      setSuccess("Application submitted successfully!");

      setTimeout(() => {
        setApplyDialogOpen(false);
        setHasApplied(true);
        resetApplyFlow();
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit application');
    }
  }


  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  if (!job) {
    return (
        <Container sx={{ py: 4 }}>
          <Alert severity="error">Job not found</Alert>
        </Container>
    );
  }

  const isGoogle = job.company_name?.toLowerCase().includes('google');
  const primaryColor = isGoogle ? '#4285F4' : '#1E3A8A';
  const secondaryColor = isGoogle ? '#34A853' : '#1E3A8A';

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const getWorkModeColor = (mode) => {
    const m = mode?.toLowerCase();
    if (m === 'remote') return 'success';
    if (m === 'hybrid') return 'warning';
    return 'primary';
  };

  const handleApplyClick = () => {
    if (hasApplied) return;
    if (isAuthenticated) {
      resetApplyFlow();
      setShowFinalForm(!hasScreeningQuestions);
      setApplyDialogOpen(true);
    } else {
      alert('Please login first to apply.');
      navigate('/login');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setShareSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setShareSnackbarOpen(false);
  };

  return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8 }}>
        {/* Hero Section */}
        <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e5e7eb', py: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#111827', mb: 1, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                  {job.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 3 }}>
                  <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 700 }}>
                    {job.company_name}
                  </Typography>
                  {job.company_verification_status?.toUpperCase() === "APPROVED" && (
                      <VerifiedRounded
                          sx={{
                            color: "#2563EB",
                            fontSize: 24,
                          }}
                      />
                  )}
                </Box>

                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ color: '#4b5563' }}>
                  {job.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.location}</Typography>
                      </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeWork sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.work_mode || 'On-site'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Payments sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>
                      {job.salary || (job.stipend ? `₹${job.stipend}` : 'Competitive')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Work sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>{job.experience_range || job.experience_level || 'Not Specified'}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="column" spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                  <Button
                      variant="contained"
                      size="large"
                      disabled={hasApplied}
                      onClick={handleApplyClick}
                      sx={{
                        background: hasApplied ? '#10b981' : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        '&:hover': {
                          background: hasApplied ? '#059669' : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          filter: hasApplied ? 'none' : 'brightness(1.1)',
                          transform: 'translateY(-2px)'
                        },
                        fontWeight: 700,
                        px: 4,
                        py: 1.5,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: hasApplied ? 'none' : `0 4px 14px ${primaryColor}40`,
                        transition: 'all 0.3s'
                      }}
                  >
                    {hasApplied ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CheckCircleOutline fontSize="small" />
                          <span>Applied</span>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Apply Now</span>
                          <Launch fontSize="small" sx={{ ml: 1 }} />
                        </Stack>
                    )}
                  </Button>
                  <Button
                      variant="outlined"
                      startIcon={<Share />}
                      onClick={handleShare}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: '#e5e7eb',
                        color: '#4b5563',
                        py: 1.2,
                        px: 4,
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }
                      }}
                  >
                    Share Job
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Job Description Card */}
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, mb: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#f8fafc', color: '#4b5563', borderRadius: '8px', display: 'flex', border: '1px solid #e5e7eb' }}>
                    <Description fontSize="small" />
                  </Box>
                  <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#111827',
                        fontSize: '1.4rem'
                      }}
                  >
                    Job Description
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Box
                    sx={{
                      color: '#374151',
                      lineHeight: 1.8,
                      '& h1, & h2, & h3': { color: '#111827', fontWeight: 700, mt: 3, mb: 1.5 },
                      '& p': { mb: 2 },
                      '& ul, & ol': { pl: 3, mb: 2 },
                      '& li': { mb: 1.5 },
                      fontSize: '1rem',
                      wordBreak: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{ __html: job.description }}
                />
              </Paper>

              {/* Requirements Card */}
              {job.requirements && (
                  <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, mb: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                      <Box sx={{ p: 1, bgcolor: '#f8fafc', color: '#4b5563', borderRadius: '8px', display: 'flex', border: '1px solid #e5e7eb' }}>
                        <List fontSize="small" />
                      </Box>
                      <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            fontSize: '1.4rem'
                          }}
                      >
                        Requirements
                      </Typography>
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Box sx={{ mt: 2 }}>
                      {Array.isArray(job.requirements) ? (
                          <Stack spacing={2}>
                            {job.requirements.map((req, index) => (
                                <Stack direction="row" spacing={2} key={index} alignItems="flex-start">
                                  <CheckCircleOutline sx={{ color: '#10b981', mt: 0.5, fontSize: '1.25rem' }} />
                                  <Typography
                                      variant="body1"
                                      sx={{
                                        color: '#374151',
                                        lineHeight: 1.8
                                      }}
                                  >
                                    {req}
                                  </Typography>
                                </Stack>
                            ))}
                          </Stack>
                      ) : (
                          <Typography sx={{ color: '#374151', whiteSpace: 'pre-line', fontSize: '1rem', lineHeight: 1.8 }}>
                            {job.requirements}
                          </Typography>
                      )}
                    </Box>
                  </Paper>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Sidebar Card: Job Overview */}
              <Stack spacing={3}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: '16px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#111827', fontSize: '1.4rem' }}>Job Overview</Typography>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '13px' }}>Posted on</Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <Schedule sx={{ fontSize: '1.25rem', color: '#94a3b8' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '15px' }}>{formatDate(job.created_at)}</Typography>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '13px' }}>Employment Type</Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <Work sx={{ fontSize: '1.25rem', color: '#94a3b8' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '15px' }}>{job.job_type || 'Full Time'}</Typography>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '13px' }}>Location</Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <LocationOn sx={{ fontSize: '1.25rem', color: '#94a3b8' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '15px' }}>{job.location || 'Remote'}</Typography>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '13px' }}>Department</Typography>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <Business sx={{ fontSize: '1.25rem', color: '#94a3b8' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '15px' }}>{job.department || (job.title?.split(' ')[0]) || 'Engineering'}</Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>

          {/* Bottom Apply CTA */}
          <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: { xs: 4, md: 6 },
                textAlign: 'center',
                borderRadius: '16px',
                bgcolor: 'white',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
              }}
          >
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: '#111827' }}>
              Ready to apply?
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#64748b', maxWidth: 600, mx: 'auto' }}>
              Join {job.company_name} and take the next step in your career. We're looking for talented individuals like you.
            </Typography>
            <Button
                variant="contained"
                size="large"
                disabled={hasApplied}
                onClick={handleApplyClick}
                sx={{
                  py: 2,
                  px: 6,
                  borderRadius: '12px',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  background: hasApplied ? '#10b981' : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  boxShadow: hasApplied ? 'none' : `0 10px 25px ${primaryColor}40`,
                  '&:hover': {
                    background: hasApplied ? '#059669' : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    filter: hasApplied ? 'none' : 'brightness(1.1)',
                    transform: 'scale(1.02)'
                  },
                  transition: 'all 0.3s'
                }}
            >
              {hasApplied ? 'Application Sent' : 'Apply Now'}
              {!hasApplied && <Launch fontSize="small" sx={{ ml: 1.5 }} />}
            </Button>
            <Typography variant="caption" sx={{ mt: 3, display: 'block', color: '#94a3b8' }}>
              Application takes less than 2 minutes
            </Typography>
          </Paper>
        </Container>

        <Snackbar
            open={shareSnackbarOpen}
            autoHideDuration={3000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              variant="filled"
              icon={<CheckCircleOutline fontSize="inherit" />}
              sx={{
                width: '100%',
                borderRadius: '12px',
                fontWeight: 600,
                bgcolor: '#10b981', // Emerald 500
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-icon': {
                  color: 'white'
                }
              }}
          >
            Job link copied successfully
          </Alert>
        </Snackbar>

        {/* Apply Dialog */}
        <Dialog
            open={applyDialogOpen}
            onClose={() => {
              setApplyDialogOpen(false);
              resetApplyFlow();
            }}
            maxWidth="md"
            fullWidth
        >
          <DialogTitle
              sx={{
                fontWeight: 800,
                fontSize: '1.35rem',
                color: '#111827',
                borderBottom: '1px solid #f1f5f9',
                pb: 2
              }}
          >
            Complete Your Application
          </DialogTitle>
          <DialogContent>
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                >
                  {error}
                </Alert>
            )}

            {success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                >
                  {success}
                </Alert>
            )}



            {hasScreeningQuestions ? (
                !showFinalForm ? (
                    <>
                      <Box
                          sx={{
                            mb: 3,
                            p: 3,
                            borderRadius: '20px',
                            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)'
                          }}
                      >
                        <Typography
                            sx={{
                              fontSize: '0.95rem',
                              fontWeight: 800,
                              color: '#1e293b',
                              mb: 0.75
                            }}
                        >
                          Experience & Background
                        </Typography>

                        <Typography
                            sx={{
                              fontSize: '0.92rem',
                              color: '#475569',
                              lineHeight: 1.7,
                              mb: 0.75
                            }}
                        >
                          Please answer a few short questions to complete your application.
                        </Typography>

                        <Typography
                            sx={{
                              fontSize: '0.8rem',
                              color: '#64748b'
                            }}
                        >
                          Your responses will be shared with the recruiter along with your application.
                        </Typography>
                      </Box>

                      <LinearProgress
                          variant="determinate"
                          value={
                            screeningQuestions.length
                                ? ((currentQuestionIndex + 1) / screeningQuestions.length) * 100
                                : 0
                          }
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            mb: 3,
                            backgroundColor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 999,
                              background: 'linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%)'
                            }
                          }}
                      />

                      {currentQuestion ? (
                          <Box
                              sx={{
                                p: 3,
                                borderRadius: '20px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                                mb: 3
                              }}
                          >
                            <Typography
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 800,
                                  color: '#2563eb',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.06em',
                                  mb: 1
                                }}
                            >
                              Question {currentQuestionIndex + 1} of {screeningQuestions.length}
                            </Typography>

                            <Typography
                                sx={{
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  color: '#0f172a',
                                  lineHeight: 1.7,
                                  mb: 2
                                }}
                            >
                              {currentQuestion.question}
                            </Typography>

                            <TextField
                                fullWidth
                                value={screeningAnswers[currentQuestion.key] || ''}
                                onChange={(e) =>
                                    handleScreeningAnswerChange(currentQuestion.key, e.target.value)
                                }
                                multiline
                                rows={4}
                                placeholder="Type your answer here..."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '14px',
                                    backgroundColor: '#f8fafc',
                                    '& fieldset': {
                                      borderColor: '#cbd5e1'
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#94a3b8'
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#2563eb'
                                    }
                                  },
                                  '& .MuiInputBase-input': {
                                    fontSize: '0.96rem',
                                    lineHeight: 1.8,
                                    color: '#0f172a'
                                  }
                                }}
                            />
                          </Box>
                      ) : null}
                    </>
                ) : (
                    <>
                      <TextField
                          fullWidth
                          label="Education"
                          multiline
                          rows={2}
                          value={education}
                          onChange={(e) => setEducation(e.target.value)}
                          margin="normal"
                          required
                          placeholder="E.g. B.Tech in Computer Science from XYZ University"
                      />

                      <TextField
                          fullWidth
                          label="Previous Experience"
                          multiline
                          rows={2}
                          value={experience}
                          onChange={(e) => setExperience(e.target.value)}
                          margin="normal"
                          required
                          placeholder="E.g. 2 years as Software Developer at ABC Corp"
                      />

                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Button variant="outlined" component="label" fullWidth sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Upload Resume (PDF Only)
                            <input
                                type="file"
                                hidden
                                accept=".pdf"
                                onChange={handleFileChange}
                            />
                          </Button>
                          <Button
                              onClick={() => setLearnMoreOpen(true)}
                              sx={{ minWidth: 'auto', ml: 1, color: '#64748b' }}
                              title="Learn More about requirements"
                          >
                            <InfoOutlined />
                          </Button>
                        </Box>
                        {resumeName && (
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: fileValidationError ? 'error.main' : 'text.secondary', fontWeight: fileValidationError ? 600 : 400 }}>
                              Selected: {resumeName}
                            </Typography>
                        )}
                        {fileValidationError && (
                            <Alert severity="warning" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                              {fileValidationError}
                            </Alert>
                        )}
                      </Box>
                    </>
                )
            ) : (
                <>
                  <TextField
                      fullWidth
                      label="Education"
                      multiline
                      rows={2}
                      value={education}
                      onChange={(e) => setEducation(e.target.value)}
                      margin="normal"
                      required
                      placeholder="E.g. B.Tech in Computer Science from XYZ University"
                  />

                  <TextField
                      fullWidth
                      label="Previous Experience"
                      multiline
                      rows={2}
                      value={experience}
                      onChange={(e) => setExperience(e.target.value)}
                      margin="normal"
                      required
                      placeholder="E.g. 2 years as Software Developer at ABC Corp"
                  />

                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Button variant="outlined" component="label" fullWidth sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Upload Resume (PDF Only)
                        <input
                            type="file"
                            hidden
                            accept=".pdf"
                            onChange={handleFileChange}
                        />
                      </Button>
                      <Button
                          onClick={() => setLearnMoreOpen(true)}
                          sx={{ minWidth: 'auto', ml: 1, color: '#64748b' }}
                          title="Learn More about requirements"
                      >
                        <InfoOutlined />
                      </Button>
                    </Box>
                    {resumeName && (
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: fileValidationError ? 'error.main' : 'text.secondary', fontWeight: fileValidationError ? 600 : 400 }}>
                          Selected: {resumeName}
                        </Typography>
                    )}
                    {fileValidationError && (
                        <Alert severity="warning" sx={{ mt: 1, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                          {fileValidationError}
                        </Alert>
                    )}
                  </Box>
                </>
            )}

          </DialogContent>
          <DialogActions
              sx={{
                px: 3,
                pb: 3,
                pt: 1,
                gap: 1.5,
              }}
          >
            <Button
                variant="outlined"
                onClick={() => {
                  setApplyDialogOpen(false);
                  resetApplyFlow();
                }}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.5,
                  borderColor: '#cbd5e1',
                  color: '#334155',
                }}
            >
              Cancel
            </Button>

            {hasScreeningQuestions ? (
                !showFinalForm ? (
                    <Button
                        onClick={handleNextScreening}
                        variant="contained"
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 800,
                          px: 3,
                          background: 'linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%)',
                          boxShadow: '0 10px 24px rgba(37, 99, 235, 0.25)',
                        }}
                    >
                      Continue
                    </Button>
                ) : (
                    <Button
                        onClick={handleApply}
                        disabled={isSubmitDisabled}
                        variant="contained"
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 800,
                          px: 3,
                          background: isSubmitDisabled ? '#e2e8f0' : 'linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%)',
                          boxShadow: isSubmitDisabled ? 'none' : '0 10px 24px rgba(37, 99, 235, 0.25)',
                        }}
                    >
                      Submit Application
                    </Button>
                )
            ) : (
                <Button
                    onClick={handleApply}
                    disabled={isSubmitDisabled}
                    variant="contained"
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 800,
                      px: 3,
                      background: isSubmitDisabled ? '#e2e8f0' : 'linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%)',
                      boxShadow: isSubmitDisabled ? 'none' : '0 10px 24px rgba(37, 99, 235, 0.25)',
                    }}
                >
                  Submit Application
                </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Learn More Modal */}
        <Dialog open={learnMoreOpen} onClose={() => setLearnMoreOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800, color: '#111827' }}>Resume Upload Requirements</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                Why only PDF?
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#475569' }}>
                Our AI resume scoring engine is optimized specifically for PDF files to ensure the highest accuracy in parsing and matching your skills with job requirements. DOC/DOCX files are currently not supported.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                Filename Guidelines
              </Typography>
              <Box component="ul" sx={{ pl: 2, color: '#475569' }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  The filename <strong>must start with a letter</strong> (A-Z or a-z).
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Filenames starting with numbers or special characters (e.g., 123Resume.pdf, _Resume.pdf, @Resume.pdf) are <strong>not allowed</strong>.
                </Typography>
                <Typography component="li" variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                  Example of a valid filename: Madhu_Resume.pdf
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setLearnMoreOpen(false)} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none' }}>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default JobDetails;